import { useState, useEffect } from 'react'
import {
  PersistentPreferences,
  DIETARY_PREFERENCES,
  AVAILABLE_APPLIANCES,
  COMMON_RESTRICTIONS,
  DEFAULT_PERSISTENT_PREFERENCES,
  Toasty
} from '@talktoast/shared'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'
import { Button, Input, Card } from '@talktoast/ui'
import GuestSignUpModal from '../auth/GuestSignUpModal'

interface QuickStartProps {
  onComplete: (preferences: PersistentPreferences) => void
  onSkip: () => void
  onShowAuth?: (authType: 'login' | 'signup') => void
  isGuest?: boolean
}

/**
 * QuickStart Component
 *
 * Single-screen consolidated setup flow that replaces the 4-step InitialSetup.
 * Users can customize key preferences OR skip entirely with smart defaults.
 *
 * Smart Defaults:
 * - Dietary: Omnivore (most flexible)
 * - Units: Imperial/Volume (US standard for home cooking)
 * - Appliances: Oven, Stovetop, Microwave (most common)
 * - Restrictions: None (can add later)
 * - Cuisine: Surprise me (explores all cuisines)
 */
export default function QuickStart({ onComplete, onSkip, onShowAuth, isGuest = false }: QuickStartProps) {
  const [preferences, setPreferences] = useState<PersistentPreferences>({
    ...DEFAULT_PERSISTENT_PREFERENCES,
    primaryDietaryPreference: 'omnivore', // Smart default
    unitsSystem: 'imperial', // Smart default for US
    measurementBias: 'volume', // Smart default for home cooking
    kitchenAppliances: ['oven', 'stovetop', 'microwave'], // Already defaulted
    cuisineStrategy: 'surprise' // Already defaulted
  })
  const [customRestriction, setCustomRestriction] = useState('')
  const [showSignUpModal, setShowSignUpModal] = useState(false)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const addRestriction = (restriction: string) => {
    if (restriction && !preferences.dietaryRestrictions.includes(restriction)) {
      setPreferences(prev => ({
        ...prev,
        dietaryRestrictions: [...prev.dietaryRestrictions, restriction]
      }))
    }
    setCustomRestriction('')
  }

  const removeRestriction = (restriction: string) => {
    setPreferences(prev => ({
      ...prev,
      dietaryRestrictions: prev.dietaryRestrictions.filter(r => r !== restriction)
    }))
  }

  const toggleAppliance = (appliance: string) => {
    setPreferences(prev => ({
      ...prev,
      kitchenAppliances: prev.kitchenAppliances.includes(appliance)
        ? prev.kitchenAppliances.filter(a => a !== appliance)
        : [...prev.kitchenAppliances, appliance]
    }))
  }

  const handleContinue = () => {
    onComplete(preferences)
  }

  const handleSkip = () => {
    // For guest users, show sign-up modal before allowing skip
    if (isGuest && onShowAuth) {
      setShowSignUpModal(true)
    } else {
      // Authenticated users can skip directly
      onSkip()
    }
  }

  const handleSkipConfirmed = () => {
    // User chose to continue as guest after seeing modal
    setShowSignUpModal(false)
    onSkip()
  }

  const handleSignUp = () => {
    setShowSignUpModal(false)
    if (onShowAuth) {
      onShowAuth('signup')
    }
  }

  const handleLogin = () => {
    setShowSignUpModal(false)
    if (onShowAuth) {
      onShowAuth('login')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-toast-gold-50 via-white to-toast-gold-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <Card className="p-6 sm:p-8 shadow-xl">
          {/* Header */}
          <div className="text-center mb-6">
            <Toasty size="lg" className="mx-auto mb-3" alt="Welcome to TalkToast" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to TalkToast!</h1>
            <p className="text-gray-600">
              Let's personalize your meal planning experience. You can customize these now or skip to start cooking!
            </p>
          </div>

          {/* Quick Setup Form - Single Screen */}
          <div className="space-y-6">
            {/* Dietary Preference */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ICONS.food.utensils className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                <h3 className="text-lg font-semibold text-gray-900">Dietary Preference</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {DIETARY_PREFERENCES.map(diet => (
                  <button
                    key={diet.value}
                    onClick={() => setPreferences(prev => ({
                      ...prev,
                      primaryDietaryPreference: diet.value
                    }))}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      preferences.primaryDietaryPreference === diet.value
                        ? 'border-toast-gold-600 bg-toast-gold-50'
                        : 'border-gray-200 hover:border-toast-gold-300'
                    }`}
                  >
                    <div className="font-medium text-sm">{diet.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Common Restrictions (Optional) */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ICONS.feedback.warning className={`${getIconSize('md')} ${getIconColor('muted')}`} />
                <h3 className="text-lg font-semibold text-gray-900">Dietary Restrictions</h3>
                <span className="text-sm text-gray-500">(Optional)</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {COMMON_RESTRICTIONS.slice(0, 8).map(restriction => (
                  <button
                    key={restriction}
                    onClick={() =>
                      preferences.dietaryRestrictions.includes(restriction)
                        ? removeRestriction(restriction)
                        : addRestriction(restriction)
                    }
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      preferences.dietaryRestrictions.includes(restriction)
                        ? 'bg-toast-gold-200 text-toast-gold-900 border-toast-gold-300'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-toast-gold-300'
                    }`}
                  >
                    {restriction}
                  </button>
                ))}
              </div>

              {/* Custom Restriction Input */}
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={customRestriction}
                  onChange={(e) => setCustomRestriction(e.target.value)}
                  placeholder="Add custom restriction..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addRestriction(customRestriction)
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => addRestriction(customRestriction)}
                  disabled={!customRestriction.trim()}
                >
                  Add
                </Button>
              </div>

              {/* Selected Restrictions */}
              {preferences.dietaryRestrictions.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-toast-gold-50 rounded-lg">
                  {preferences.dietaryRestrictions.map(restriction => (
                    <span
                      key={restriction}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-toast-gold-200 rounded-full text-sm"
                    >
                      {restriction}
                      <button
                        type="button"
                        onClick={() => removeRestriction(restriction)}
                        className="text-toast-gold-600 hover:text-toast-gold-800"
                      >
                        <ICONS.actions.close className={getIconSize('xs')} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Measurement System */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ICONS.utility.filters className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                <h3 className="text-lg font-semibold text-gray-900">Measurement System</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  key="imperial"
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    unitsSystem: 'imperial',
                    measurementBias: 'volume'
                  }))}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    preferences.unitsSystem === 'imperial'
                      ? 'border-toast-gold-600 bg-toast-gold-50'
                      : 'border-gray-200 hover:border-toast-gold-300'
                  }`}
                >
                  <div className="font-semibold mb-1">US/Imperial</div>
                  <div className="text-sm text-gray-600">Cups, tablespoons, Fahrenheit</div>
                </button>
                <button
                  key="metric"
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    unitsSystem: 'metric',
                    measurementBias: 'weight'
                  }))}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    preferences.unitsSystem === 'metric'
                      ? 'border-toast-gold-600 bg-toast-gold-50'
                      : 'border-gray-200 hover:border-toast-gold-300'
                  }`}
                >
                  <div className="font-semibold mb-1">Metric</div>
                  <div className="text-sm text-gray-600">Grams, milliliters, Celsius</div>
                </button>
              </div>
            </div>

            {/* Kitchen Appliances */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ICONS.food.chef className={`${getIconSize('md')} ${getIconColor('primary')}`} />
                <h3 className="text-lg font-semibold text-gray-900">Kitchen Appliances</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AVAILABLE_APPLIANCES.map(appliance => (
                  <button
                    key={appliance.value}
                    onClick={() => toggleAppliance(appliance.value)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      preferences.kitchenAppliances.includes(appliance.value)
                        ? 'border-toast-gold-600 bg-toast-gold-50'
                        : 'border-gray-200 hover:border-toast-gold-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {preferences.kitchenAppliances.includes(appliance.value) && (
                        <ICONS.actions.confirm className={`${getIconSize('sm')} text-toast-gold-600`} />
                      )}
                      <span className="text-lg">{appliance.emoji}</span>
                      <span className="font-medium text-sm">{appliance.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 space-y-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleContinue}
              className="bg-toast-gold-600 hover:bg-toast-gold-700 text-white"
            >
              Continue with My Preferences
            </Button>
            <Button
              variant="ghost"
              size="lg"
              fullWidth
              onClick={handleSkip}
              className="text-gray-600 hover:text-gray-900"
            >
              Skip for Now - Use Smart Defaults
            </Button>
          </div>

          {/* Smart Defaults Info */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-2">
              <ICONS.utility.idea className={`${getIconSize('sm')} text-gray-500 mt-0.5`} />
              <div className="text-sm text-gray-600">
                <strong>Smart Defaults:</strong> Omnivore diet, US measurements, common appliances.
                You can customize these anytime in Settings.
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Guest Sign-Up Modal */}
      <GuestSignUpModal
        isOpen={showSignUpModal}
        onClose={handleSkipConfirmed}
        onSignUp={handleSignUp}
        onLogin={handleLogin}
        triggerContext="quick-start-skip"
      />
    </div>
  )
}
